import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PlaceholderImage from './PlaceholderImage';

const PublicShare: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [photoInfo, setPhotoInfo] = useState<any>(null);

  useEffect(() => {
    if (!token) return;

    const fetchPhotoInfo = async () => {
      try {
        const response = await fetch(`/api/share/${token}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError('Ссылка не найдена');
          } else if (response.status === 410) {
            setError('Срок действия ссылки истёк');
          } else {
            setError('Произошла ошибка при загрузке');
          }
          return;
        }

        const data = await response.json();
        setPhotoInfo(data.photo);
      } catch (err) {
        console.error('Error fetching photo info:', err);
        setError('Не удалось загрузить фотографию');
      } finally {
        setLoading(false);
      }
    };

    fetchPhotoInfo();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md">
          <div className="text-center">
            <div className="text-red-600 text-6xl mb-4">⚠</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Ошибка</h1>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gray-800 px-6 py-4">
            <h1 className="text-xl font-semibold text-white">Просмотр фотографии</h1>
            <p className="text-sm text-gray-400 mt-1">
              Публичная ссылка • Просмотров: {photoInfo?.views || 0}
            </p>
          </div>

          {/* Image */}
          <div className="bg-gray-900 p-8 flex items-center justify-center min-h-[500px]">
            <img
              src={`/api/share/${token}/image`}
              alt="Shared photo"
              className="max-w-full max-h-[600px] object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const placeholder = target.parentElement?.querySelector('.placeholder-wrapper');
                if (placeholder) {
                  placeholder.classList.remove('hidden');
                }
              }}
            />
            <div className="placeholder-wrapper hidden">
              <PlaceholderImage
                className="w-64 h-64"
                text="Изображение недоступно"
              />
            </div>
          </div>

          {/* Info */}
          {photoInfo && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                {photoInfo.photo_date && (
                  <div>
                    <dt className="font-medium text-gray-500">Дата съёмки</dt>
                    <dd className="mt-1 text-gray-900">
                      {new Date(photoInfo.photo_date).toLocaleString('ru-RU')}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="font-medium text-gray-500">ID фотографии</dt>
                  <dd className="mt-1 text-gray-900">#{photoInfo.id}</dd>
                </div>
              </dl>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Эта фотография была предоставлена вам по защищённой ссылке</p>
        </div>
      </div>
    </div>
  );
};

export default PublicShare;